import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlert, mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { cn } from '../../lib/utils'
import { TRADE_META, formatCost, worksiteCostLow, worksiteCostHigh, yearBudget } from '../../lib/capex-utils'
import type { Worksite, TradeCategory } from './types'
import { WorksiteRow } from './WorksiteRow'
import { useCapexCurrency } from './CapexCurrencyContext'

interface TradeSectionProps {
  trade: string
  worksites: Worksite[]
  horizonStart: number
  horizonEnd: number
  org: string
  isCollapsed: boolean
  onToggle: () => void
  isReadOnly: boolean
  onUpdateWorksite: (uid: string, body: Record<string, unknown>, ifMatch: string) => void
  onDeleteWorksite: (uid: string) => void
  hasNonCompliance?: boolean
  onQuote?: (pkg: import('./types').WorkPackage) => void
  onDeleteQuote?: (pkg: import('./types').WorkPackage) => void
  onAddPackage?: (worksiteUid: string) => void
  onEditWorksite?: (worksite: import('./types').Worksite) => void
  onEditPackage?: (pkg: import('./types').WorkPackage) => void
}

export const TradeSection = React.memo(function TradeSection({
  trade,
  worksites,
  horizonStart,
  horizonEnd,
  org,
  isCollapsed,
  onToggle,
  isReadOnly,
  onUpdateWorksite,
  onDeleteWorksite,
  hasNonCompliance: hasNonComplianceProp,
  onQuote,
  onDeleteQuote,
  onAddPackage,
  onEditWorksite,
  onEditPackage,
}: TradeSectionProps) {
  const capexCurrency = useCapexCurrency()
  const { t } = useTranslation('capex')

  const tradeMeta = TRADE_META[trade as TradeCategory]
  const borderColorClass = tradeMeta?.color ?? 'border-border'

  const years: number[] = []
  for (let y = horizonStart; y <= horizonEnd; y++) {
    years.push(y)
  }

  const totalCost = useMemo(
    () =>
      worksites
        .filter((ws) => ws.status !== 'ignored')
        .reduce((sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2, 0),
    [worksites]
  )

  const annualBudgets = useMemo(
    () => years.map((year) => yearBudget(worksites, year)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worksites, horizonStart, horizonEnd]
  )

  // Non-compliance: computed by parent from compliance evaluations, defaults to false
  const hasNonCompliance = hasNonComplianceProp ?? false

  const tradeLabel = t(`trade.${trade}`, trade)

  return (
    <>
      {/* Trade header row */}
      <tr
        className="bg-muted/40 hover:bg-muted/50 cursor-pointer border-b border-border"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={!isCollapsed}
        aria-label={`Toggle ${tradeLabel} section`}
        data-testid={`trade-section-${trade}`}
      >
        <td
          className={cn(
            'sticky left-0 z-10 bg-muted/40 border-r border-border px-3 py-2 w-80 min-w-80',
          )}
        >
          <div className="flex items-center gap-2">
            {/* Collapse toggle */}
            <span className="text-muted-foreground shrink-0">
              {isCollapsed ? (
                <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
              ) : (
                <AppIcon path={mdiChevronDown} size="sm" tone="muted" />
              )}
            </span>

            {/* Trade color strip */}
            <span className={cn('w-2 h-4 rounded-full border-l-4 shrink-0', borderColorClass)} />

            {/* Trade label */}
            <span className="text-sm font-semibold text-foreground">{tradeLabel}</span>

            {/* Worksite count */}
            <span className="text-xs text-muted-foreground">
              ({worksites.length})
            </span>

            {/* Total cost */}
            <span className="text-xs font-medium text-foreground ml-auto">
              {formatCost(totalCost, capexCurrency)}
            </span>

            {/* Non-compliance badge */}
            {hasNonCompliance && (
              <span
                className="flex items-center gap-1 text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded ml-1"
                aria-label={t('compliance_issues', 'Non-compliance')}
                data-testid={`trade-noncompliance-${trade}`}
              >
                <AppIcon path={mdiAlert} size="xs" />
                {t('compliance_issues', 'Non-compliance')}
              </span>
            )}
          </div>
        </td>

        {/* Annual subtotal cells */}
        {years.map((year, i) => {
          const budget = annualBudgets[i]
          return (
            <td
              key={year}
              className="border-r border-border/30 px-2 py-2 text-center"
            >
              {budget > 0 && (
                <span className="text-xs text-muted-foreground font-medium">
                  {formatCost(budget, capexCurrency)}
                </span>
              )}
            </td>
          )
        })}
      </tr>

      {/* Worksite rows — shown when not collapsed */}
      {!isCollapsed &&
        worksites.map((ws) => (
          <WorksiteRow
            key={ws.uid}
            worksite={ws}
            horizonStart={horizonStart}
            horizonEnd={horizonEnd}
            org={org}
            isReadOnly={isReadOnly}
            onUpdate={onUpdateWorksite}
            onDelete={onDeleteWorksite}
            onQuote={onQuote}
            onDeleteQuote={onDeleteQuote}
            onAddPackage={onAddPackage}
            onEditWorksite={onEditWorksite}
            onEditPackage={onEditPackage}
          />
        ))}
    </>
  )
})
